import { useCallback } from 'react';
import { useInterviewStore, selectBooleanQuestionInfo } from '../stores/interviewStore';
import { useWebSocketStore } from '../stores/websocketStore';
import { useSalaryStore } from '../stores/salaryStore';
import { ApplicationData } from '../types/interview';
import { getUiCopy } from '../i18n/copy';

export const useInterview = () => {
  const store = useInterviewStore();
  const websocketStore = useWebSocketStore();
  const salaryStore = useSalaryStore();

  const handleApplicationValidation = useCallback(async (appData: ApplicationData) => {
    // Check the flag to prevent duplicate starts from Strict Mode double-invocations.
    if (!store.session.interviewStartRequested) {
      // Persist application data so it can be used elsewhere (job title, etc.)
      store.setApplicationData(appData);

      // Set the flag immediately to create a lock and prevent race conditions.
      store.updateSession({ interviewStartRequested: true });

      // Rely on the websocket store's built-in queueing.
      // This will send the `start_interview` message as soon as the connection is established.
      websocketStore.startInterview(appData);
    }
  }, [store, websocketStore]);

  const handleUserInput = useCallback((input: string, inputMethod?: string) => {
    if (input.trim()) {
      store.addMessage(input, 'user');
      store.handleInterviewEvent('ANSWER_SUBMITTED');
      websocketStore.sendUserMessage(
        input,
        inputMethod,
        store.interviewData.currentQuestionIndex
      );
    }
  }, [store, websocketStore]);

  const handleBooleanAnswer = useCallback((answer: boolean) => {
    const copy = getUiCopy(store.session.uiLocale);
    const answerText = answer ? copy.yes : copy.no;
    const booleanInfo = selectBooleanQuestionInfo(store);

    // Keep the input area hidden until the server sends the next question/phase change.
    store.updateUI({ booleanAnswerGiven: true, showBooleanButtons: false, showInputArea: false });
    store.addMessage(answerText, 'user');

    if (booleanInfo) {
      websocketStore.submitBooleanAnswer(booleanInfo.questionIndex, answer);
    }
  }, [store, websocketStore]);

  const handleRatingSubmit = useCallback((rating: number) => {
    store.updateSession({ hasRated: true });
    store.updateRating({ rating, hasSubmitted: true });
    websocketStore.submitRating(rating);
    // User sees "Thank you for feedback" via a ChatMessage component when hasRated is true
  }, [store, websocketStore]);

  const handleSalarySubmission = useCallback((salaryInput: string) => {
    salaryStore.processSalary(salaryInput);
    if (salaryStore.isValidInput) {
      store.addMessage(getUiCopy(store.session.uiLocale).salarySubmitted(salaryInput), 'user');
      websocketStore.sendSalaryResponse(salaryInput);
    }
  }, [salaryStore, store, websocketStore]);

  return {
    ...store,
    // Actions
    handleApplicationValidation,
    handleUserInput,
    handleBooleanAnswer,
    handleRatingSubmit,
    handleSalarySubmission,
    setThinking: store.setThinking,
    showSkeleton: store.showSkeleton,
    hideSkeleton: store.hideSkeleton,
    // WebSocket connection status
    connectionStatus: websocketStore.connectionStatus,
    // Selector-like properties for convenience
    canSubmitInput:
      !store.ui.inputDisabled &&
      !store.session.isProcessing &&
      store.ui.inputValue.trim().length > 0,
    // Add isSalaryQuestion to the returned properties
    isSalaryQuestion: store.ui.isSalaryQuestion,
    // Salary properties from salaryStore
    salaryProcessing: salaryStore.isProcessing,
    salaryValidationErrors: salaryStore.validationErrors,
  };
};
